import { useCallback, useEffect, useState } from "react";
import { getFileFromFirebaseStorage } from "../../../api/firebase/firestoreService";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { logDataToConsole } from "../../../utils/customLogger";

/**
 * Manages attachment File objects for the current medical PA order.
 *
 * Automatically fetches the clinical document as a `File` when the accumulator
 * order's clinical document path changes. Additional files can be fetched on
 * demand via `fetchFile`.
 *
 * @returns {object} Hook state and helpers
 * @returns {File[]} files - Resolved File objects accumulated so far.
 * @returns {boolean} isLoading - `true` while a fetch is in progress.
 * @returns {(refPath: string) => Promise<void>} fetchFile - Fetches a file
 *   from Firebase Storage for the given reference path and appends it to
 *   `files`.
 */
export const useAttachmentFiles = () => {
  const { generalOrderDetails } = useDataAccumulatorForSingleOrder();
  const order = generalOrderDetails?.order ?? null;
  const [files, setFiles] = useState<File[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchFile = useCallback(async (refPath: string) => {
    setIsLoading(true);
    try {
      const file = await getFileFromFirebaseStorage(refPath);
      setFiles((prev) =>
        prev.some((f) => f.name === file.name) ? prev : [...prev, file],
      );
    } catch (err) {
      logDataToConsole("Error fetching file", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setFiles([]);
    if (order?.clinical_document?.file_path) {
      fetchFile(order.clinical_document.file_path);
    }
  }, [order?.clinical_document?.file_path, fetchFile]);

  useEffect(() => {
    setDocuments(files);
  }, [files]);

  return { files, documents, setDocuments, isLoading, fetchFile };
};
